/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router";
import {
  ControlledDateTimePicker,
  ControlledInput,
  ControlledSelect,
  ControlledTextarea,
  DynamicFormInput,
  FormUpload,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useUploadImage } from "../../store/useUploadImage";
import { useAlert } from "../../store/useAlert";
import { useLottery } from "../../store/useLottery";
import { DropLocationInputItem } from "../../components/form/DynamicFormInput";
import { useMarketPricing } from "../../store/useMarketPricing";
import { useDropLocations } from "../../store/useDropLocations";
import { usePartners } from "../../store/usePartners";
import { useVouchers } from "../../store/useVouchers";
import { useCommunities } from "../../store/useCommunities";
import { Switch } from "@material-tailwind/react";
import { LotteryVoucherModal } from "./components/LotteryVoucherModal";
import { LotteryMobilePreview } from "./components/LotteryMobilePreview";

type Inputs = {
  partner: string;
  en_title: string;
  nl_title: string;
  en_description: string;
  nl_description: string;
  en_prizeTitle: string;
  nl_prizeTitle: string;
  en_prizeDescription: string;
  nl_prizeDescription: string;
  drawDate: Date;
  startsAt?: Date | null;
  isRecurring: boolean;
  recurringInterval: "DAILY" | "WEEKLY" | "MONTHLY";
  numberOfWinners?: number;
  allowMultipleEntries: boolean;
};

// Update ICondition interface to support includeAllMaterials
interface ICondition {
  conditionId: number;
  conditionType: "MATERIAL" | "DONATION";
  // Material fields
  material?: string;
  includeAllMaterials?: boolean;
  materialRequirementType?: "WEIGHT" | "DROP_COUNT";
  // Donation fields
  community?: string;
  donationRequirementType?: "DONATION_COUNT" | "DONATION_VALUE";
  // Common field
  value: number;
  includeStatiegeldDrops: boolean;
}

// Interface for our UI state management
interface IConditionCard {
  id: string;
  items: {
    id: string;
    itemId: string;
    itemType: "material" | "community";
    itemName: string;
    requirementType: string;
    value: string;
  }[];
  includeStatiegeldDrops: boolean;
}

function AddLotteryScreen() {
  /** Hooks */
  const { addLottery, addedLottery, clearAddedLottery } = useLottery();
  const {
    activeVouchers,
    getPartnerActiveVouchers,
    clearPartnerActiveVouchers,
  } = useVouchers();
  const navigate = useNavigate();
  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>();
  const { uploadImage, clearUploadedImage, uploadedImage, thumbnailImage } =
    useUploadImage();
  const { setAlert } = useAlert();
  const { getAllMarketPrices, marketPrices } = useMarketPricing();
  const { dropLocations } = useDropLocations();
  const { getAllPartners, allPartners } = usePartners();
  const { communities, getCommunities } = useCommunities();

  /** State */
  const [image, setImage] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isRecurring, setIsRecurring] = useState<boolean>(false);
  const [cities, setCities] = useState<DropLocationInputItem[]>([]);
  const [locations, setLocations] = useState<DropLocationInputItem[]>([]);
  const [locationType, setLocationType] = useState<"cities" | "locations">(
    "cities"
  );
  // New state for condition cards
  const [conditionCards, setConditionCards] = useState<IConditionCard[]>([]);
  const [isVoucherModalOpen, setIsVoucherModalOpen] = useState(false);
  /** Preview: which locale to show in mobile preview */
  const [previewLocale, setPreviewLocale] = useState<"en" | "nl">("en");
  /** Preview: image URL (object URL from file or uploaded path) */
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  /** Effects */
  useEffect(() => {
    getAllMarketPrices();
    getAllPartners(1, 1000);
    getCommunities(1, 1000);
    return () => {
      clearPartnerActiveVouchers();
    };
  }, []);

  useEffect(() => {
    if (addedLottery) {
      clearAddedLottery();
      clearUploadedImage();
      navigate(-1);
    }
  }, [addedLottery]);

  useEffect(() => {
    if (watch("partner")) {
      getPartnerActiveVouchers(watch("partner"));
    }
  }, [watch("partner")]);

  // Preview image: show selected file as object URL, or uploaded image path
  useEffect(() => {
    if (image) {
      const url = URL.createObjectURL(image);
      setPreviewImageUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewImageUrl(uploadedImage);
  }, [image, uploadedImage]);

  /** Memoized Values */
  const marketPricesToSelect = useMemo(() => {
    return (
      marketPrices
        ?.filter((price) => price?.isActive !== false)
        ?.map((price) => ({
          value: price?._id as string,
          label: price?.name as string,
        })) ?? []
    );
  }, [marketPrices]);

  const lotteryCitiesToSelect = useMemo(() => {
    if (!dropLocations?.length) return [];
    const selectedCityIds = cities.map((city) => String(city.id));
    let locationsToSelect: { value: string; label: string }[] = [];
    for (let location of dropLocations) {
      if (locationsToSelect?.some((loc) => loc?.value === location?.city))
        continue;
      if (!selectedCityIds.includes(location?.city as string)) {
        locationsToSelect.push({
          label: location?.city as string,
          value: location?.city as string,
        });
      }
    }
    return locationsToSelect;
  }, [dropLocations, cities]);

  const lotteryLocationsToSelect = useMemo(() => {
    if (!dropLocations?.length) return [];
    const selectedLocationIds = locations.map((location) =>
      String(location.id)
    );
    return dropLocations
      .filter(
        (location) => !selectedLocationIds.includes(String(location?._id))
      )
      .map((location) => ({
        label: location?.cost_place as string,
        value: location?._id as string,
      }));
  }, [dropLocations, locations]);

  const communitiesToSelect = useMemo(() => {
    return (
      communities?.map((community) => ({
        value: community?._id as string,
        label: community?.name as string,
      })) ?? []
    );
  }, [communities]);

  /** Callbacks */
  const goBack = () => navigate(-1);

  const selectedPartnerId = watch("partner");
  const selectedPartner = useMemo(() => {
    if (!selectedPartnerId) return null;
    return (
      allPartners?.find((partner) => partner?._id === selectedPartnerId) ?? null
    );
  }, [allPartners, selectedPartnerId]);

  const refreshActiveVouchers = () => {
    if (selectedPartnerId) {
      getPartnerActiveVouchers(selectedPartnerId);
    }
  };

  const addAllCities = () => {
    const allCitiesToAdd = lotteryCitiesToSelect.map((city) => ({
      id: city.value,
      value: city.value,
      label: city.label,
    }));
    setCities(allCitiesToAdd);
  };

  const addAllLocations = () => {
    const allLocationsToAdd = lotteryLocationsToSelect.map((location) => ({
      id: location.value,
      value: location.label,
      label: location.label,
    }));
    setLocations(allLocationsToAdd);
  };

  const handleLocationTypeChange = (newType: "cities" | "locations") => {
    setLocationType(newType);
    setCities([]);
    setLocations([]);
  };

  // Condition card management functions
  const addConditionCard = () => {
    const newCard: IConditionCard = {
      id: `card-${Date.now()}`,
      items: [],
      includeStatiegeldDrops: false,
    };
    setConditionCards([...conditionCards, newCard]);
  };

  const removeConditionCard = (cardId: string) => {
    setConditionCards(conditionCards.filter((card) => card.id !== cardId));
  };

  const toggleStatiegeldDrops = (cardId: string) => {
    setConditionCards(
      conditionCards.map((card) =>
        card.id === cardId
          ? { ...card, includeStatiegeldDrops: !card.includeStatiegeldDrops }
          : card
      )
    );
  };

  // Update addConditionItem to prevent multiple materials per card
  const addConditionItem = (
    cardId: string,
    itemId: string,
    itemType: "material" | "community",
    itemName: string,
    isAnyMaterial?: boolean
  ) => {
    setConditionCards(
      conditionCards.map((card) => {
        if (card.id !== cardId) return card;

        if (itemType === "material") {
          // Check if "Any Materials" already exists in this card
          const hasAnyMaterial = card.items.some(
            (item) => item.itemType === "material" && item.itemId === "__any__"
          );

          // If trying to add "Any Materials" but other materials exist
          if (
            isAnyMaterial &&
            card.items.some((item) => item.itemType === "material")
          ) {
            return card; // Don't add
          }

          // If "Any Materials" already exists, don't add any other material
          if (hasAnyMaterial && !isAnyMaterial) {
            return card; // Don't add
          }

          // If trying to add "Any Materials", remove all existing materials first
          if (isAnyMaterial) {
            return {
              ...card,
              items: [
                ...card.items.filter((item) => item.itemType !== "material"),
                {
                  id: `${itemType}-${Date.now()}`,
                  itemId,
                  itemType,
                  itemName,
                  requirementType: "",
                  value: "",
                  isAnyMaterial: true,
                },
              ],
            };
          }
        }

        return {
          ...card,
          items: [
            ...card.items,
            {
              id: `${itemType}-${Date.now()}`,
              itemId,
              itemType,
              itemName,
              requirementType: "",
              value: "",
              ...(isAnyMaterial ? { isAnyMaterial: true } : {}),
            },
          ],
        };
      })
    );
  };

  const removeConditionItem = (cardId: string, itemId: string) => {
    setConditionCards(
      conditionCards.map((card) =>
        card.id === cardId
          ? {
              ...card,
              items: card.items.filter((item) => item.id !== itemId),
            }
          : card
      )
    );
  };

  const updateConditionItemRequirementType = (
    cardId: string,
    itemId: string,
    requirementType: string
  ) => {
    setConditionCards(
      conditionCards.map((card) =>
        card.id === cardId
          ? {
              ...card,
              items: card.items.map((item) =>
                item.id === itemId
                  ? { ...item, requirementType, value: requirementType === "drops" ? "1" : item.value }
                  : item
              ),
            }
          : card
      )
    );
  };

  const updateConditionItemValue = (
    cardId: string,
    itemId: string,
    value: string
  ) => {
    setConditionCards(
      conditionCards.map((card) =>
        card.id === cardId
          ? {
              ...card,
              items: card.items.map((item) =>
                item.id === itemId ? { ...item, value } : item
              ),
            }
          : card
      )
    );
  };

  const validateConditions = (): boolean => {
    // Check if any card is incomplete
    for (const card of conditionCards) {
      if (card.items.length === 0) {
        setAlert({
          isOpen: true,
          message: `Please add at least one material or donation to each condition`,
          severity: "error",
        });
        return false;
      }

      // Check that all items have requirement type and value
      for (const item of card.items) {
        if (!item.requirementType) {
          setAlert({
            isOpen: true,
            message: `Please select a requirement type for ${item.itemName}`,
            severity: "error",
          });
          return false;
        }

        if (!item.value || parseFloat(item.value) <= 0) {
          setAlert({
            isOpen: true,
            message: `Please enter a valid value for ${item.itemName}`,
            severity: "error",
          });
          return false;
        }
      }
    }
    return true;
  };

  // Convert condition cards to backend format
  const prepareConditionsForSubmission = (): ICondition[] => {
    const conditions: ICondition[] = [];
    conditionCards.forEach((card, cardIdx) => {
      const conditionId = cardIdx + 1;
      card.items.forEach((item) => {
        const condition: ICondition = {
          conditionId,
          conditionType: item.itemType === "material" ? "MATERIAL" : "DONATION",
          value: parseFloat(item.value),
          includeStatiegeldDrops: card.includeStatiegeldDrops,
        };

        if (item.itemType === "material") {
          // If "Any Materials" is selected, set includeAllMaterials: true
          if (item.itemId === "__any__") {
            console.log("entered");
            condition.includeAllMaterials = true;
          } else {
            condition.material = item.itemId;
            condition.includeAllMaterials = false;
          }
          condition.materialRequirementType =
            item.requirementType === "weight" ? "WEIGHT" : "DROP_COUNT";
        } else if (item.itemType === "community") {
          condition.community = item.itemId;
          condition.donationRequirementType =
            item.requirementType === "value"
              ? "DONATION_VALUE"
              : "DONATION_COUNT";
        }
        conditions.push(condition);
      });
    });

    return conditions;
  };

  const onSubmit = async (data: Inputs) => {
    if (!image && !uploadedImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      setSubmitted(true);
      return;
    }

    if (!uploadedImage && image) {
      uploadImage(image, true);
      setSubmitted(true);
      return;
    }

    // Validate conditions if there are any
    if (conditionCards.length > 0 && !validateConditions()) {
      return;
    }

    const body: any = {
      ...data,
      isRecurring,
      locationType: locationType === "cities" ? "CITY" : "LOCATION",
    };

    // An untouched date picker leaves an empty value behind. Sending that
    // would make the backend try to cast "" to a Date; dropping the key means
    // the lottery is simply live as soon as it is ACTIVE, which is the old
    // behaviour and the right default.
    if (!data.startsAt) {
      delete body.startsAt;
    }

    if (uploadedImage) {
      body.image = uploadedImage;
    }
    if (thumbnailImage) {
      body.thumbnail = thumbnailImage;
    }

    // Add conditions if there are any
    if (conditionCards.length > 0) {
      body.conditions = prepareConditionsForSubmission();
    }

    if (locationType === "cities" && cities?.length) {
      body.cities = cities.map((input) => String(input.id));
    }
    if (locationType === "locations" && locations?.length) {
      body.locationIds = locations.map((input) => String(input.id));
    }

    addLottery(body);
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  return (
    <div className="px-2 sm:px-4 md:px-6">
      <div className="flex flex-row items-center mb-4 sm:mb-6">
        <h1 className="font-satoshi text-xl sm:text-2xl font-bold text-dark">
          Add new lottery
        </h1>
      </div>
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:flex-1 lg:min-w-0 bg-white  border-gray-border drop-shadow-none rounded-xl mb-2 sm:mb-0">
          <div className="w-full border-b border-gray-border px-4 sm:px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              New lottery
            </p>
          </div>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-full px-4 sm:px-7 py-5 sm:py-7"
          >
            {/* Partner Selection */}
            <ControlledSelect
              name="partner"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.partner)}
              label="Partner"
              // Carriers are parcel partners only: they never back a lottery,
              // and they are managed on their own page.
              options={allPartners
                // A lottery saved against a carrier before this rule keeps
                // showing it, so editing one does not silently blank the field.
                ?.filter(
                  (partner) =>
                    !partner?.isCourier || partner?._id === watch("partner")
                )
                ?.map((partner) => ({
                  _id: partner?._id as string,
                  title: partner?.name as string,
                }))}
              placeholder="Pick Partner here"
              inputClassName="w-full"
              className="w-full"
            />
            {/* Never for a carrier: a carrier is out of the app's partner
                list by rule, not by mistake. */}
            {selectedPartner &&
              selectedPartner.showInApp === false &&
              !selectedPartner.isCourier && (
                <p className="font-satoshi text-sm text-red-600 mb-4">
                  This partner is not visible in the app (Show in App is
                  disabled).
                </p>
              )}
            {typeof activeVouchers === "number" && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                <p
                  className={`font-satoshi text-sm ${
                    activeVouchers === 0 ? "text-red-600" : "text-dark"
                  }`}
                >
                  {activeVouchers} active vouchers from this Partner
                </p>
                <PrimaryButton
                  label="Add lottery voucher"
                  size="sm"
                  className="mt-3 sm:mt-0 sm:ml-4"
                  onClick={() => {
                    if (!selectedPartnerId) {
                      setAlert({
                        isOpen: true,
                        message:
                          "Please select a partner before adding a voucher.",
                        severity: "error",
                      });
                      return;
                    }
                    setIsVoucherModalOpen(true);
                  }}
                />
              </div>
            )}

            <p className="font-satoshi text-xs text-gray-500 mb-6 -mt-3">
              A lottery stays hidden in the app until at least one voucher is
              assigned to it.
            </p>

            {/* Lottery Details */}
            <ControlledInput
              control={control}
              error={Boolean(errors?.en_title)}
              label="Title (English version)"
              placeholder="Lottery title (English)"
              name="en_title"
              rules={{ required: true }}
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.nl_title)}
              label="Title (Dutch version)"
              placeholder="Lottery title (Dutch)"
              name="nl_title"
              rules={{ required: true }}
            />
            <ControlledTextarea
              name="en_description"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.en_description)}
              label="Description (English version)"
              placeholder="Lottery description here (English)..."
              inputClassName="min-h-[180px]"
            />
            <ControlledTextarea
              name="nl_description"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.nl_description)}
              label="Description (Dutch version)"
              placeholder="Lottery description here (dutch)..."
              inputClassName="min-h-[180px]"
            />

            {/* Prize Details */}
            <ControlledInput
              control={control}
              error={Boolean(errors?.en_prizeTitle)}
              label="Prize title (English)"
              placeholder="Prize title (English)"
              name="en_prizeTitle"
              rules={{ required: true }}
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.nl_prizeTitle)}
              label="Prize title (Dutch)"
              placeholder="Prize title (Dutch)"
              name="nl_prizeTitle"
              rules={{ required: true }}
            />
            <ControlledTextarea
              name="en_prizeDescription"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.en_prizeDescription)}
              label="Prize description (English version)"
              placeholder="Prize description here (English)..."
              inputClassName="min-h-[180px]"
            />
            <ControlledTextarea
              name="nl_prizeDescription"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.nl_prizeDescription)}
              label="Prize description (Dutch version)"
              placeholder="Prize description here (dutch)..."
              inputClassName="min-h-[180px]"
            />

            {/* Image Upload */}
            <FormUpload
              label="Lottery Prize image"
              onChange={onImageChange}
              value={image}
              error={submitted && !image && !uploadedImage}
            />

            {/* Location Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location Type
              </label>
              <select
                value={locationType}
                onChange={(e) =>
                  handleLocationTypeChange(
                    e.target.value as "cities" | "locations"
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="cities">Cities</option>
                <option value="locations">Locations</option>
              </select>
            </div>

            {/* Cities or Locations */}
            {locationType === "cities" && (
              <div className="mb-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 space-y-2 sm:space-y-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Cities
                  </label>
                  {lotteryCitiesToSelect.length > 0 && (
                    <button
                      type="button"
                      onClick={addAllCities}
                      className="w-full sm:w-auto px-3 py-1 text-sm bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-colors"
                    >
                      Add All Cities
                    </button>
                  )}
                </div>
                <DynamicFormInput
                  key={`cities-${cities.length}`}
                  generateIds={false}
                  label=""
                  inputs={cities}
                  setInputs={setCities}
                  options={lotteryCitiesToSelect}
                />
              </div>
            )}

            {locationType === "locations" && (
              <div className="mb-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 space-y-2 sm:space-y-0">
                  <label className="block text-sm font-medium text-gray-700">
                    Locations
                  </label>
                  {lotteryLocationsToSelect.length > 0 && (
                    <button
                      type="button"
                      onClick={addAllLocations}
                      className="w-full sm:w-auto px-3 py-1 text-sm bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-colors"
                    >
                      Add All Locations
                    </button>
                  )}
                </div>
                <DynamicFormInput
                  key={`locations-${locations.length}`}
                  generateIds={false}
                  label=""
                  inputs={locations}
                  setInputs={setLocations}
                  options={lotteryLocationsToSelect}
                />
              </div>
            )}

            {/* Accepted Conditions */}
            <div className="mb-8">
              <label className="block text-lg font-medium text-gray-700 mb-4">
                Accepted Conditions
              </label>

              {conditionCards.length === 0 && (
                <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
                  <p className="text-gray-500">
                    No conditions added yet. Click the button below to add a
                    condition.
                  </p>
                </div>
              )}

              {/* Condition Cards */}
              {conditionCards.map((card, index) => (
                <div key={card.id} className="mb-6">
                  {/* "AND" between main conditions - OUTSIDE card */}
                  {index > 0 && (
                    <div className="flex justify-center items-center my-2">
                      <span className="px-4 py-1 bg-gray-200 text-gray-700 rounded font-bold">
                        AND
                      </span>
                    </div>
                  )}
                  <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    {/* Card Header */}
                    <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex flex-wrap items-center justify-between">
                      <h3 className="font-medium text-gray-700">
                        Condition {index + 1}
                      </h3>
                      <button
                        type="button"
                        onClick={() => removeConditionCard(card.id)}
                        className="text-red-500 hover:text-red-700"
                        aria-label="Remove condition"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                    {/* Card Body */}
                    <div className="p-4">
                      {/* "+ Add Material" Button */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          + Add Material
                        </label>
                        <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2">
                          <select
                            id={`material-select-${card.id}`}
                            className="w-full sm:flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            defaultValue=""
                            disabled={card.items.some(
                              (item) =>
                                item.itemType === "material" &&
                                item.itemId === "__any__"
                            )}
                          >
                            <option value="" disabled>
                              Select material
                            </option>
                            {!card.items.some(
                              (item) => item.itemType === "material"
                            ) && <option value="__any__">Any Materials</option>}
                            {/* Show individual materials if "Any Materials" is not selected */}
                            {!card.items.some(
                              (item) =>
                                item.itemType === "material" &&
                                item.itemId === "__any__"
                            ) &&
                              marketPricesToSelect.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                          </select>
                          <button
                            type="button"
                            className="w-full sm:w-auto px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                            onClick={() => {
                              const select = document.getElementById(
                                `material-select-${card.id}`
                              ) as HTMLSelectElement;
                              if (!select || !select.value) return;
                              if (select.value === "__any__") {
                                addConditionItem(
                                  card.id,
                                  "__any__",
                                  "material",
                                  "Any Materials",
                                  true
                                );
                                select.value = "";
                                return;
                              }
                              const selectedOption = marketPricesToSelect.find(
                                (option) => option.value === select.value
                              );
                              if (selectedOption) {
                                addConditionItem(
                                  card.id,
                                  selectedOption.value,
                                  "material",
                                  selectedOption.label
                                );
                                select.value = ""; // Reset selection
                              }
                            }}
                            disabled={card.items.some(
                              (item) =>
                                item.itemType === "material" &&
                                item.itemId === "__any__"
                            )}
                          >
                            + Add Material
                          </button>
                        </div>
                        {card.items.some(
                          (item) =>
                            item.itemType === "material" &&
                            item.itemId === "__any__"
                        ) ? (
                          <p className="text-xs text-gray-500 mt-1">
                            "Any Materials" is selected. Remove it to add
                            specific materials.
                          </p>
                        ) : card.items.some(
                            (item) => item.itemType === "material"
                          ) ? (
                          <p className="text-xs text-gray-500 mt-1">
                            Specific materials selected. Cannot add "Any
                            Materials".
                          </p>
                        ) : (
                          <p className="text-xs text-gray-500 mt-1">
                            Select "Any Materials" or specific materials (but
                            not both).
                          </p>
                        )}
                      </div>
                      {/* "+ Add Community" Button */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          + Add Community
                        </label>
                        <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2">
                          <select
                            id={`community-select-${card.id}`}
                            className="w-full sm:flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            defaultValue=""
                          >
                            <option value="" disabled>
                              Select community
                            </option>
                            {communitiesToSelect.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="w-full sm:w-auto px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors font-semibold"
                            onClick={() => {
                              const select = document.getElementById(
                                `community-select-${card.id}`
                              ) as HTMLSelectElement;
                              if (!select || !select.value) return;
                              const selectedOption = communitiesToSelect.find(
                                (option) => option.value === select.value
                              );
                              if (selectedOption) {
                                addConditionItem(
                                  card.id,
                                  selectedOption.value,
                                  "community",
                                  selectedOption.label
                                );
                                select.value = ""; // Reset selection
                              }
                            }}
                          >
                            + Add Community
                          </button>
                        </div>
                      </div>

                      {/* List of added items */}
                      {card.items.length === 0 ? (
                        <p className="text-sm text-gray-500 italic mb-4">
                          No materials or donations added yet
                        </p>
                      ) : (
                        <div className="space-y-4 mb-4">
                          {card.items.map((item, idx) => (
                            <div key={item.id}>
                              {/* "OR" between subconditions - OUTSIDE item card */}
                              {idx > 0 && (
                                <div className="flex justify-center items-center my-2">
                                  <span className="px-4 py-1 bg-gray-100 text-gray-700 rounded font-bold">
                                    OR
                                  </span>
                                </div>
                              )}
                              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                                <div className="flex flex-wrap items-center justify-between mb-2">
                                  <span className="font-medium text-gray-700 mr-2">
                                    {item.itemName}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      removeConditionItem(card.id, item.id)
                                    }
                                    className="text-red-500 hover:text-red-700"
                                    aria-label="Remove item"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-4 w-4"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      {item.itemType === "material"
                                        ? "Requirement Type"
                                        : "Donation Type"}
                                    </label>
                                    <select
                                      value={item.requirementType}
                                      onChange={(e) =>
                                        updateConditionItemRequirementType(
                                          card.id,
                                          item.id,
                                          e.target.value
                                        )
                                      }
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                    >
                                      <option value="">Select type</option>
                                      {item.itemType === "material" ? (
                                        <>
                                          <option value="weight">
                                            Weight (kg)
                                          </option>
                                          <option value="drops">
                                            Number of drops
                                          </option>
                                        </>
                                      ) : (
                                        <>
                                          <option value="donations">
                                            Number of donations
                                          </option>
                                          <option value="value">
                                            Value donated (€)
                                          </option>
                                        </>
                                      )}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Value
                                    </label>
                                    <input
                                      type="number"
                                      value={item.requirementType === "drops" ? "1" : item.value}
                                      readOnly={item.requirementType === "drops"}
                                      onChange={(e) =>
                                        item.requirementType !== "drops" &&
                                        updateConditionItemValue(
                                          card.id,
                                          item.id,
                                          e.target.value
                                        )
                                      }
                                      placeholder={
                                        item.itemType === "material"
                                          ? item.requirementType === "weight"
                                            ? "Weight in kg"
                                            : item.requirementType === "drops"
                                              ? "Number of drops"
                                              : "Enter value"
                                          : item.requirementType === "value"
                                            ? "Amount in euros"
                                            : item.requirementType ===
                                                "donations"
                                              ? "Number of donations"
                                              : "Enter value"
                                      }
                                      className={`w-full px-2 py-1 text-sm border border-gray-300 rounded-md${item.requirementType === "drops" ? " bg-gray-100 cursor-not-allowed" : ""}`}
                                      min="0"
                                      step={
                                        item.itemType === "material"
                                          ? item.requirementType === "weight"
                                            ? "0.01"
                                            : "1"
                                          : item.requirementType === "value"
                                            ? "0.01"
                                            : "1"
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Include Statiegeld Switch - only show if at least one material item exists */}
                      {card.items.some(
                        (item) => item.itemType === "material"
                      ) && (
                        <div className="mt-4">
                          <Switch
                            crossOrigin={"anonymous"}
                            color="teal"
                            ripple
                            label="Include Statiegeld Drops"
                            checked={card.includeStatiegeldDrops}
                            onChange={() => toggleStatiegeldDrops(card.id)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Move "Add Condition" button underneath the conditions */}
              <div className="flex justify-center sm:justify-end mt-4">
                <button
                  type="button"
                  onClick={addConditionCard}
                  className="w-full sm:w-auto px-4 py-2 bg-teal-500 text-white font-medium rounded-md hover:bg-teal-600 transition-colors"
                >
                  + Add Condition
                </button>
              </div>
            </div>

            {/* Is Recurring */}
            {conditionCards.length > 0 && (
              <div className="mb-6">
                <Switch
                  crossOrigin={"anonymous"}
                  color="teal"
                  ripple
                  label="Is Recurring"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e?.target.checked)}
                />
              </div>
            )}

            {/* Number of Winners */}
            <ControlledInput
              control={control}
              error={Boolean(errors?.numberOfWinners)}
              label="Number of winners"
              placeholder="Enter number of winners"
              name="numberOfWinners"
              type="number"
              rules={{
                min: {
                  value: 1,
                  message: "Number of winners must be at least 1",
                },
              }}
            />

            {/* Allow Multiple Entries */}
            <div className="mb-6">
              <Switch
                crossOrigin={"anonymous"}
                color="teal"
                ripple
                label="Allow Multiple Entries"
                checked={watch("allowMultipleEntries") || false}
                onChange={(e) => {
                  setValue("allowMultipleEntries", e?.target?.checked || false);
                }}
              />
            </div>

            {/* Start Date — when the lottery becomes visible in the app */}
            <ControlledDateTimePicker
              name="startsAt"
              rules={{ required: false }}
              control={control}
              error={Boolean(errors?.startsAt)}
              label="Start date (optional)"
              placeholder="Leave empty to publish immediately..."
            />

            {/* Draw Date */}
            <ControlledDateTimePicker
              name="drawDate"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.drawDate)}
              label="Draw date"
              placeholder="Schedule the date to draw the lottery winner here..."
            />

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-end space-y-2 sm:space-y-0">
              <SecondaryButton
                type="button"
                onClick={goBack}
                label="Cancel"
                className="w-full sm:w-auto"
              />
              <PrimaryButton
                type="submit"
                className="w-full sm:w-auto sm:ml-4"
                label="Save"
              />
            </div>
          </form>
        </div>

        {/* Mobile preview – same look as in the app */}
        <div className="w-full hidden lg:w-[380px] lg:shrink-0">
          <div className="bg-white border border-gray-border drop-shadow-md rounded-lg sticky top-4">
            <div className="border-b border-gray-border px-4 sm:px-5 py-3">
              <p className="font-satoshi font-medium text-base text-dark">
                App preview
              </p>
              <p className="font-satoshi text-sm text-gray-text mt-0.5">
                How this lottery will look in the app
              </p>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setPreviewLocale("en")}
                  className={`px-3 py-1 text-sm font-medium rounded-md ${
                    previewLocale === "en"
                      ? "bg-droppie-oranje text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewLocale("nl")}
                  className={`px-3 py-1 text-sm font-medium rounded-md ${
                    previewLocale === "nl"
                      ? "bg-droppie-oranje text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  NL
                </button>
              </div>
            </div>
            <div className="p-4">
              <LotteryMobilePreview
                title={
                  previewLocale === "en"
                    ? (watch("en_title") ?? "")
                    : (watch("nl_title") ?? "")
                }
                description={
                  previewLocale === "en"
                    ? (watch("en_description") ?? "")
                    : (watch("nl_description") ?? "")
                }
                imageUrl={previewImageUrl}
                locale={previewLocale}
              />
            </div>
          </div>
        </div>
      </div>
      <LotteryVoucherModal
        open={isVoucherModalOpen}
        onClose={() => setIsVoucherModalOpen(false)}
        partnerId={selectedPartnerId as string | undefined}
        partnerName={selectedPartner?.name}
        onCreated={refreshActiveVouchers}
      />
    </div>
  );
}

export default withProtectedRoute(AddLotteryScreen);
